import React from 'react';

/**
 * `<Picture>` — WebP-first image with automatic fallback.
 *
 * Assumes a matching `.webp` sits beside the original file (same basename,
 * different extension), which matches the convention set by the 2026-04
 * bundle-optimisation pass (issue #682). If no `.webp` exists, browsers just
 * load the fallback.
 *
 * Use this instead of `<img>` when:
 * - the source is a photo (JPG) — WebP saves ~20–30 %
 * - the component sits above the fold or is LCP-critical
 *
 * Logos (flat-colour PNGs) are typically *smaller* as pngquant-compressed
 * PNGs than as WebPs — don't bother wrapping those.
 *
 * Example:
 *   <Picture src="/images/hero-1.jpg" alt="..." className="hero" />
 */
function Picture({ src, alt, className, loading, style, ...rest }) {
  // Derive the WebP peer from the src path. Works for .jpg/.jpeg/.png.
  const webpSrc = src.replace(/\.(jpe?g|png)$/i, '.webp');

  // If src didn't match a known extension, just render a plain img.
  if (webpSrc === src) {
    return <img src={src} alt={alt} className={className} loading={loading} style={style} {...rest} />;
  }

  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img src={src} alt={alt} className={className} loading={loading} style={style} {...rest} />
    </picture>
  );
}

export default Picture;
